import {
  addAcuteLearningPathToUser,
  getAcuteLearningPaths,
  removeAcuteLearningPathFromUser,
} from "../../api";
import { createShadowRoot, html } from "../../lib";
import { createLoader } from "../loader";
import { createWarning } from "../warning";

const template = document.createElement("template");

const optionsContainerId = "options";
const closeButtonId = "close";

template.innerHTML = html`
  <style>
    * {
      box-sizing: border-box;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    button {
      background-color: transparent;
      border-color: rgb(57, 73, 171);
      border-radius: 4px;
      border-width: 1px;
      color: rgb(57, 73, 171);
      font-family: Roboto, sans-serif;
      font-size: 0.875rem;
      font-weight: 500;
      height: 36px;
      letter-spacing: 0.0892857143em;
      padding: 0px 16px;
      text-transform: uppercase;
    }

    button:hover {
      background-color: transparent;
    }

    dialog {
      border-color: rgba(0, 0, 0, 0.4);
      border-radius: 4px;
      border-width: 1px;
      height: 100vh;
      margin: auto;
      min-width: 100px;
      padding: 20px;
    }

    .dialog-wrapper {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
    }
  </style>

  <dialog>
    <div class="dialog-wrapper">
      <div class="dialog-header">
        <p>Learning Path Options</p>
        <button id="${closeButtonId}">Close</button>
      </div>
      <div id="${optionsContainerId}" class="dialog-content"></div>
    </div>
  </dialog>
`;

class El extends HTMLElement {
  #closeButton;
  #dialog;
  #enrolledContainer;
  #optionsContainer;
  #showButton;

  constructor() {
    super();

    const { getElementById, querySelector } = createShadowRoot(this, template);

    this.#dialog = querySelector("dialog");

    this.#closeButton = getElementById(closeButtonId);
    this.#optionsContainer = getElementById(optionsContainerId);
  }

  set enrolled(learningPaths) {
    this.#enrolledContainer.replaceChildren(...learningPaths);
  }

  set open(show) {
    this.#showButton.onclick = show;
  }

  set close(close) {
    this.#closeButton.onclick = close;
  }

  set optionsList(list) {
    this.#optionsContainer.replaceChildren(list);
  }

  showDialog() {
    this.#dialog.showModal();
  }

  closeDialog() {
    this.#dialog.close();
  }
}

customElements.define("o-learning-path-acute-input-dialog", El);

export const createAcuteLearningPathInputDialog = ({
  closedCb,
  saDisplayName,
  userPrincipalName,
}) => {
  const el = new El();

  const open = async enrolled => {
    el.showDialog();
    el.optionsList = createLoader("Loading learning path options");

    const learningPaths = await getAcuteLearningPaths();
    if (learningPaths instanceof Error) {
      el.optionsList = createWarning("Unable to load acute options");
      return 0;
    }

    const createCheckbox = createCheckboxFactory({
      enrolled,
      saDisplayName,
      userPrincipalName,
    });

    const ul = document.createElement("ul");
    ul.replaceChildren(...learningPaths.map(createCheckbox));

    el.optionsList = ul;
  };

  const close = async () => {
    el.closeDialog();
    await closedCb();
  };

  el.close = close;

  return {
    el,
    open,
  };
};

const createCheckboxFactory = ({
  enrolled,
  saDisplayName,
  userPrincipalName,
}) => {
  const enrolledIdList = enrolled.map(learningPath => learningPath.id);

  const createCheckbox = learningPath => {
    const input = document.createElement("input");
    input.type = "checkbox";
    input.name = learningPath.id;
    input.id = learningPath.id;

    if (enrolledIdList.includes(learningPath.id)) {
      input.setAttribute("checked", "");
    }

    input.onchange = async () => {
      if (input.checked) {
        const result = await addAcuteLearningPathToUser({
          saDisplayName,
          userPrincipalName,
          learningPath,
        });
        if (result instanceof Error) {
          return 0;
        }
        return 0;
      }

      const result = await removeAcuteLearningPathFromUser({
        saDisplayName,
        userPrincipalName,
        learningPath,
      });
      if (result instanceof Error) {
        return 0;
      }
      return 0;
    };

    const label = document.createElement("label");
    label.setAttribute("for", learningPath.id);
    label.textContent = learningPath.displayName;

    const li = document.createElement("li");
    li.replaceChildren(input, " ", label);

    return li;
  };

  return createCheckbox;
};
